/**
 * PLImpactTab — Phase 6a.14 per Frontend Spec §21.8.3.
 *
 * The 3rd results tab. Surfaces `aggregations.plImpact` in the canonical
 * Big4 IFRS 16 53(e)/53(f) disclosure format used in Coca-Cola İçecek
 * 2025, Ford Otomotiv 2025, and Turkcell 2025 audited financial
 * statements.
 *
 * Layout (single `<Card>`):
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  Title + IFRS 53(e)/53(f) subtitle          │
 *   ├─────────────────────────────────────────────┤
 *   │  GİDER KALEMLERİ                            │
 *   │    Faiz / Amortisman / Değişken Kira        │
 *   │    ─── TOPLAM GİDER                         │
 *   │  ─── (Separator)                            │
 *   │  KAZANÇ / KAYIPLAR                          │
 *   │    Modifikasyon / Sonlandırma / Kur         │
 *   │    ─── TOPLAM KAZANÇ / KAYIP                │
 *   │  ═══ (Thick Separator)                      │
 *   │  NET KÂR/ZARAR ETKİSİ              (...)    │
 *   │  ─── İç Kontrol: …  ✓                       │
 *   └─────────────────────────────────────────────┘
 *
 * Sign convention (audit-paper, IFRS-aligned):
 *   • Expenses always render as positive (debits — outflows are implied
 *     by the section label).
 *   • Gain/Loss lines are SIGNED. Negative (a loss) wraps in parentheses
 *     and uses `text-destructive`.
 *   • Net P&L Effect is signed. Lease portfolios are typically net
 *     negative — render in parens + red.
 *
 * Per-contract breakdown is DEFERRED to Phase 6a.18 (unified drill-down
 * across all tabs). This phase shows engagement-level totals only.
 *
 * Self-reconciliation footer (auditor confidence signal):
 *   computedNet = totalGainsLosses - totalExpenses
 *   varianceKurus = round(|backendNet - computedNet|, 2)
 *   If varianceKurus <= 0.01 → green ✓  (Bulgu AC tolerance, 1 kuruş)
 *   Otherwise                → amber warning with variance
 *
 * Mini-fix AY (Spec §21.8.3.6, Bulgu AC) — backend Decimal.js vs
 * frontend JS Number arithmetic produces sub-kuruş drift on complex
 * P&L computations. The prior strict `< 0.01` threshold raised a
 * cosmetic amber warning on perfectly-normal engagements (e.g.
 * `-2.877.563,35` backend vs `-2.877.563,34` computed → displayed
 * variance "0,01 TRY" but the underlying float subtraction lands at
 * `0.01000000000…X`, strictly above 0,01).
 *
 * The fix rounds the variance to kuruş precision (2 decimals) BEFORE
 * comparing, then accepts ≤ 0,01 TRY. This:
 *   1. Mirrors what the auditor sees — `formatCurrency(variance.toFixed(2))`
 *      already rounds for display; the gate now uses the same rounded
 *      value, so display and decision can never disagree.
 *   2. Eliminates IEEE 754 drift sensitivity around the 0,01 boundary
 *      (`0.0100000000005` rounds to `0.01` → reconciles).
 *   3. Preserves the 1 kuruş audit tolerance — anything strictly above
 *      0,01 TRY (after rounding) still surfaces as amber, e.g. 0,02
 *      remains a genuine reconciliation failure.
 */

import { Info } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency, formatSigned } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  EngagementAggregations,
  EngagementHistoryEntry,
  MovementRowType,
  PLImpactSummary,
} from "@/types";

// EXCEL-UI-ENHANCE-01 Item 9 (UI) — component-level P&L↔movement tie-out
// tolerance. Mirrors the kuruş-level threshold the Excel side + Çapraz
// Kontrol sheet use (`crossCheckSheet.ts` TOLERANCE = 0.01). Pure
// presentation arithmetic; no engine math.
const PL_TIE_OUT_TOLERANCE = 0.01;

interface OutletContext {
  engagement: EngagementHistoryEntry;
}

export function PLImpactTab() {
  const { t } = useTranslation();
  const { engagement } = useOutletContext<OutletContext>();
  const pl = engagement.lastResult?.aggregations.plImpact;
  const movement = engagement.lastResult?.aggregations.movement;
  const currency = engagement.functionalCurrency;

  if (!pl) {
    return (
      <Card className="p-8" data-testid="pl-impact-empty">
        <p className="text-sm italic text-muted-foreground">
          {t("plImpact.emptyState")}
        </p>
      </Card>
    );
  }

  const totalExpenses = sumStrings([
    pl.interestExpense,
    pl.depreciationExpense,
    pl.variableLeaseExpense,
  ]);
  const totalGainsLosses = sumStrings([
    pl.modificationGainLoss,
    pl.terminationGainLoss,
    pl.fxGainLoss,
  ]);
  const computedNet = totalGainsLosses - totalExpenses;
  const backendNet = Number(pl.netPLEffect);
  const variance = Math.abs(backendNet - computedNet);
  // Mini-fix AY (Bulgu AC) — round the variance to kuruş (2 decimals)
  // BEFORE comparing. Rationale:
  //   • Mirrors what the auditor sees: `formatCurrency(variance.toFixed(2))`
  //     already rounds for the displayed value, so a 0,01 display can
  //     never collide with a "non-reconciling" gate decision.
  //   • Eliminates IEEE 754 drift sensitivity at the 0,01 boundary —
  //     `Math.abs(-99.99 - (-100))` evaluates to `0.010000000000005116`
  //     in JS Number, which the prior strict `< 0.01` (and even a
  //     widened `<= 0.01`) would have rejected as a mismatch despite
  //     being mathematically exactly 0,01 TRY.
  //   • Preserves the spec's 1 kuruş audit tolerance — a true 0,02 TRY
  //     variance rounds to 0.02 → fails the ≤ 0.01 gate → still amber.
  const varianceKurus = Number.isFinite(variance)
    ? Math.round(variance * 100) / 100
    : NaN;
  const reconciles = Number.isFinite(varianceKurus) && varianceKurus <= 0.01;

  return (
    <Card className="p-8 glow-card" data-testid="pl-impact-tab">
      <header className="mb-6">
        <h3 className="text-lg font-semibold">
          {t("plImpact.section.title")}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t("plImpact.section.subtitle")}
        </p>
      </header>

      <ExpensesSection pl={pl} subtotal={totalExpenses} currency={currency} />

      <Separator className="my-6" />

      <GainsLossesSection
        pl={pl}
        subtotal={totalGainsLosses}
        currency={currency}
        movement={movement}
      />

      <Separator className="my-6 h-px bg-foreground" />

      <NetEffectRow netPLEffect={pl.netPLEffect} currency={currency} />

      <ReconciliationFooter
        reconciles={reconciles}
        computedNet={computedNet}
        backendNet={backendNet}
        variance={variance}
        currency={currency}
      />

      {/* EXCEL-UI-ENHANCE-01 Item 9 (UI) — component-level P&L↔movement
          tie-out. Sits below the engagement-net reconciliation pill so
          the auditor can drill from "Net dengeli" to the line-by-line
          tie. Pure presentation arithmetic (Number-only); the same
          components are recomputed in `crossCheckSheet.ts plComputedNet`
          on the Excel side, so the two surfaces agree to the cent. */}
      {movement !== undefined && (
        <ComponentTieOutSection
          pl={pl}
          movement={movement}
          currency={currency}
        />
      )}

      {/* EXCEL-UI-ENHANCE-01 Item 8 (UI) — per-contract P&L breakdown,
          collapsed by default. Renders only when at least one row is
          present so blank-engagement views stay clean. */}
      {pl.perContractBreakdown.length > 0 && (
        <PerContractBreakdownAccordion
          breakdown={pl.perContractBreakdown}
          currency={currency}
        />
      )}
    </Card>
  );
}

// ─── Section: Expenses (always-positive debits) ─────────────────────────────

interface ExpensesSectionProps {
  pl: PLImpactSummary;
  subtotal: number;
  currency: string;
}

function ExpensesSection({ pl, subtotal, currency }: ExpensesSectionProps) {
  const { t } = useTranslation();
  return (
    <section data-testid="pl-expenses">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {t("plImpact.expenses.label")}
      </h4>
      <div className="space-y-2">
        <LineRow
          label={t("plImpact.expenses.interestExpense")}
          amount={pl.interestExpense}
          currency={currency}
          testId="pl-interest"
        />
        <LineRow
          label={t("plImpact.expenses.depreciationExpense")}
          amount={pl.depreciationExpense}
          currency={currency}
          testId="pl-depreciation"
        />
        <LineRow
          label={t("plImpact.expenses.variableLeaseExpense")}
          amount={pl.variableLeaseExpense}
          currency={currency}
          testId="pl-variable"
        />
        <SubtotalRow
          label={t("plImpact.expenses.subtotal")}
          amount={subtotal.toFixed(2)}
          currency={currency}
          testId="pl-expenses-subtotal"
        />
      </div>
    </section>
  );
}

// ─── Section: Gains / Losses (signed) ───────────────────────────────────────

interface GainsLossesSectionProps {
  pl: PLImpactSummary;
  subtotal: number;
  currency: string;
  // EXCEL-UI-ENHANCE-01 Item 10 (UI) — movement aggregator. When supplied,
  // the FX K/Z parent line gets §23 + §28 indented sub-lines that sum to
  // the parent. Optional for back-compat with any caller that hasn't
  // threaded it through (none today — `PLImpactTab` always supplies it).
  movement: EngagementAggregations["movement"] | undefined;
}

function GainsLossesSection({
  pl,
  subtotal,
  currency,
  movement,
}: GainsLossesSectionProps) {
  const { t } = useTranslation();
  // EXCEL-UI-ENHANCE-01 Item 10 (UI) — compute the §23 / §28 sub-line
  // amounts the same way `plImpactSheet.ts addFxSubRow` does on the
  // Excel side: sum the corresponding MovementRowType across ROU Cost +
  // Liability (ROU AccDep carries no FX per IAS 21.23(b)), then negate
  // so the breakdown's sign matches the P&L gain/loss convention (a
  // positive gain shrinks the liability, which is signed-negative on
  // the movement). By construction `§23 + §28 == pl.fxGainLoss` to the
  // cent on the same engagement; the sub-rows are rendered immediately
  // under the parent so the auditor can tie each component to a
  // Hareket row.
  const fxBreakdown =
    movement !== undefined ? computeFxBreakdown(movement) : undefined;
  return (
    <section data-testid="pl-gains-losses">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {t("plImpact.gainsLosses.label")}
      </h4>
      <div className="space-y-2">
        <LineRow
          label={t("plImpact.gainsLosses.modificationGainLoss")}
          amount={pl.modificationGainLoss}
          currency={currency}
          testId="pl-modification"
          signed
          labelAdornment={
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  size={12}
                  className="text-muted-foreground cursor-help"
                  data-testid="pl-modification-info"
                  aria-label={t("plImpact.gainsLosses.modificationGainLoss")}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                {t("plImpact.gainsLosses.modificationGainLossTooltip")}
              </TooltipContent>
            </Tooltip>
          }
        />
        <LineRow
          label={t("plImpact.gainsLosses.terminationGainLoss")}
          amount={pl.terminationGainLoss}
          currency={currency}
          testId="pl-termination"
          signed
        />
        <LineRow
          label={t("plImpact.gainsLosses.fxGainLoss")}
          amount={pl.fxGainLoss}
          currency={currency}
          testId="pl-fx"
          signed
        />
        {/* EXCEL-UI-ENHANCE-01 Item 10 (UI) — §23 + §28 indented FX
            sub-lines that sum to the parent Kur K/Z line above. */}
        {fxBreakdown !== undefined && (
          <>
            <FxSubLine
              label={t("plImpact.gainsLosses.fxRemeasurement", {
                defaultValue: "Kur Yeniden Ölçüm (IAS 21 §23)",
              })}
              amount={fxBreakdown.remeasurement}
              currency={currency}
              testId="pl-fx-remeasurement"
            />
            <FxSubLine
              label={t("plImpact.gainsLosses.fxRevaluation", {
                defaultValue: "Kur Değerleme (IAS 21 §28)",
              })}
              amount={fxBreakdown.revaluation}
              currency={currency}
              testId="pl-fx-revaluation"
            />
          </>
        )}
        <SubtotalRow
          label={t("plImpact.gainsLosses.subtotal")}
          amount={subtotal.toFixed(2)}
          currency={currency}
          testId="pl-gains-losses-subtotal"
          signed
        />
      </div>
    </section>
  );
}

/**
 * EXCEL-UI-ENHANCE-01 Item 10 (UI) — indented italic sub-row under the
 * parent Kur K/Z line. Visual treatment mirrors Item 5's payment-split
 * sub-rows on Movement so the auditor recognises the "this composes the
 * parent" idiom across tabs.
 */
function FxSubLine({
  label,
  amount,
  currency,
  testId,
}: {
  label: string;
  amount: number;
  currency: string;
  testId: string;
}) {
  const isNegative = Number.isFinite(amount) && amount < 0;
  return (
    <div
      className="flex justify-between items-center pl-6"
      data-testid={testId}
    >
      <span className="text-sm italic text-muted-foreground">{label}</span>
      <span
        className={cn(
          "text-sm italic font-mono tabular-nums text-muted-foreground",
          isNegative && "text-destructive/70",
        )}
      >
        {formatSigned(amount.toFixed(2), currency)}
      </span>
    </div>
  );
}

// ─── Net P&L Effect (final headline row) ────────────────────────────────────

function NetEffectRow({
  netPLEffect,
  currency,
}: {
  netPLEffect: string;
  currency: string;
}) {
  const { t } = useTranslation();
  const netN = Number(netPLEffect);
  const isNegative = Number.isFinite(netN) && netN < 0;
  // EXCEL-UI-ENHANCE-01 Item 12 (UI) — direction word-label adjacent to
  // the Net figure. Negative → "zarar etkisi" (loss); positive → "kâr
  // etkisi" (gain); exactly zero → omit (no direction). The colour
  // styling (text-destructive on negative) is unchanged; this just adds
  // the word the auditor reads alongside the figure. Mirrors the Excel
  // Item 12 wording in `plImpactSheet.ts` line 144-150.
  const direction = !Number.isFinite(netN)
    ? null
    : netN < 0
      ? t("plImpact.netEffect.directionLoss", {
          defaultValue: "zarar etkisi",
        })
      : netN > 0
        ? t("plImpact.netEffect.directionGain", {
            defaultValue: "kâr etkisi",
          })
        : null;
  return (
    <div
      className="flex justify-between items-center"
      data-testid="pl-net-effect"
    >
      <span className="text-lg font-bold">{t("plImpact.netEffect.label")}</span>
      <span className="inline-flex items-baseline gap-2">
        <span
          className={cn(
            "text-lg font-bold font-mono tabular-nums",
            isNegative && "text-destructive",
          )}
          data-testid="pl-net-effect-amount"
        >
          {formatSigned(netPLEffect, currency)}
        </span>
        {direction !== null && (
          <span
            className={cn(
              "text-sm italic",
              isNegative ? "text-destructive" : "text-green-700 dark:text-green-300",
            )}
            data-testid="pl-net-effect-direction"
          >
            {direction}
          </span>
        )}
      </span>
    </div>
  );
}

// ─── Reconciliation footer (auditor self-check) ────────────────────────────

interface ReconciliationFooterProps {
  reconciles: boolean;
  computedNet: number;
  backendNet: number;
  variance: number;
  currency: string;
}

/**
 * Phase BB5-4 — compact pill in the card's bottom-right corner with a
 * hover tooltip. Replaces the full-width inline reconciliation row that
 * had a "development debug" feel for audit-grade reporting.
 *
 * Phase BB6-5 — the pill text now reads "Hesap dengeli / farkı var"
 * (auditor vocabulary, not "iç kontrol" jargon). The success tooltip
 * is a fixed-user-facing explainer; the failure tooltip keeps the
 * interpolated variance values so an auditor can act on a mismatch.
 * Pill renders unconditionally (no hover-gated opacity) so it never
 * disappears into the chrome.
 */
function ReconciliationFooter({
  reconciles,
  computedNet,
  backendNet,
  variance,
  currency,
}: ReconciliationFooterProps) {
  const { t } = useTranslation();
  // Silence the unused-binding lint for `computedNet`: the success
  // tooltip is now a fixed explainer rather than the value-interpolated
  // template, but the argument is still part of the public component
  // signature in case the failure tooltip ever needs it inline.
  void computedNet;
  const tooltipContent = reconciles
    ? t("plImpact.reconciliation.successTooltip")
    : t("plImpact.reconciliation.mismatch", {
        backendValue: formatCurrency(backendNet.toFixed(2)),
        localValue: formatCurrency(computedNet.toFixed(2)),
        variance: formatCurrency(variance.toFixed(2)),
        currency,
      });
  return (
    <div
      className="flex justify-end mt-4 pt-3 border-t"
      data-testid="pl-reconciliation"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          {reconciles ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-1 text-sm font-medium text-green-400"
              data-testid="pl-reconciliation-success"
            >
              {t("plImpact.reconciliation.passed")}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-sm font-medium text-destructive"
              data-testid="pl-reconciliation-mismatch"
            >
              {t("plImpact.reconciliation.failed")}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// ─── Row primitives ─────────────────────────────────────────────────────────

interface LineRowProps {
  label: string;
  amount: string;
  currency: string;
  testId: string;
  /**
   * Treat the amount as signed (gain/loss). Negative values wrap in
   * parens + `text-destructive`. Expense lines pass `signed=false`
   * because the section label already implies the debit direction.
   */
  signed?: boolean;
  /**
   * BB1 — optional trailing slot rendered immediately after the label.
   * Used for info-tooltip icons on rows whose value is non-obvious to
   * an auditor (e.g. the modificationGainLoss row commonly reading 0.00
   * for remeasurement-style modifications, IFRS 16 §44).
   */
  labelAdornment?: React.ReactNode;
}

function LineRow({
  label,
  amount,
  currency,
  testId,
  signed,
  labelAdornment,
}: LineRowProps) {
  const num = Number(amount);
  const isNegative = signed && Number.isFinite(num) && num < 0;
  return (
    <div
      className="flex justify-between items-center"
      data-testid={testId}
    >
      <span className="text-sm flex items-center gap-1.5">
        {label}
        {labelAdornment}
      </span>
      <span
        className={cn(
          "text-sm font-mono tabular-nums",
          isNegative && "text-destructive",
        )}
      >
        {signed ? formatSigned(amount, currency) : formatCurrency(amount, currency)}
      </span>
    </div>
  );
}

interface SubtotalRowProps extends LineRowProps {}

function SubtotalRow({
  label,
  amount,
  currency,
  testId,
  signed,
}: SubtotalRowProps) {
  const num = Number(amount);
  const isNegative = signed && Number.isFinite(num) && num < 0;
  return (
    <div
      className="flex justify-between items-center pt-2 mt-2 border-t border-border"
      data-testid={testId}
    >
      <span className="text-sm font-semibold">{label}</span>
      <span
        className={cn(
          "text-sm font-semibold font-mono tabular-nums",
          isNegative && "text-destructive",
        )}
      >
        {signed ? formatSigned(amount, currency) : formatCurrency(amount, currency)}
      </span>
    </div>
  );
}

// ─── EXCEL-UI-ENHANCE-01 Item 9 (UI) — component-level P&L↔movement tie-out ─

/**
 * Compact tie-out block under the engagement-level reconciliation pill.
 * Pins three rows the auditor most often reconciles against the
 * Hareket tab:
 *
 *   • Faiz Gideri        ↔ Liability INTEREST_ACCRUAL
 *   • Amortisman Gideri  ↔ ROU AccDep DEPRECIATION
 *   • Kur K/Z            ↔ negate(Σ FX_REMEASUREMENT + FX_REVALUATION
 *                                  across ROU Cost + Liability)
 *
 * Sign convention mirrors `crossCheckSheet.ts plComputedNet`: expenses
 * are positive on both surfaces; FX gain/loss flips sign relative to
 * the liability/ROU movement signs (per IAS 21.23 — a gain reduces the
 * liability, signed-negative on the movement).
 *
 * Tolerance is the same kuruş-level value the Excel Çapraz Kontrol
 * sheet uses (`crossCheckSheet.ts TOLERANCE`) so a row that passes
 * here passes there.
 */
function ComponentTieOutSection({
  pl,
  movement,
  currency,
}: {
  pl: PLImpactSummary;
  movement: NonNullable<EngagementAggregations["movement"]>;
  currency: string;
}) {
  const { t } = useTranslation();
  const interestMovement = findMovementAmount(
    movement.leaseLiability.rows,
    "INTEREST_ACCRUAL",
  );
  const depreciationMovement = findMovementAmount(
    movement.rouAccumulatedDepreciation.total.rows,
    "DEPRECIATION",
  );
  const fxBreakdownTotal = (() => {
    const breakdown = computeFxBreakdown(movement);
    return breakdown.remeasurement + breakdown.revaluation;
  })();
  const rows: Array<{
    label: string;
    plLabel: string;
    movementLabel: string;
    plAmount: number;
    movementAmount: number;
    testId: string;
  }> = [
    {
      label: t("plImpact.expenses.interestExpense"),
      plLabel: "P&L",
      movementLabel: "INTEREST_ACCRUAL",
      plAmount: Number(pl.interestExpense) || 0,
      movementAmount: interestMovement,
      testId: "pl-tie-out-interest",
    },
    {
      label: t("plImpact.expenses.depreciationExpense"),
      plLabel: "P&L",
      movementLabel: "DEPRECIATION",
      plAmount: Number(pl.depreciationExpense) || 0,
      movementAmount: depreciationMovement,
      testId: "pl-tie-out-depreciation",
    },
    {
      label: t("plImpact.gainsLosses.fxGainLoss"),
      plLabel: "P&L",
      movementLabel: "Σ FX (Hareket)",
      plAmount: Number(pl.fxGainLoss) || 0,
      // Excel Item 9 sign convention — see `plImpactSheet.ts addFxSubRow`
      // and `crossCheckSheet.ts plComputedNet`. The fxBreakdown sums are
      // already negated to match the P&L gain/loss sign (see
      // `computeFxBreakdown`), so this is a direct comparison.
      movementAmount: fxBreakdownTotal,
      testId: "pl-tie-out-fx",
    },
  ];
  return (
    <section
      className="mt-6 pt-4 border-t"
      data-testid="pl-component-tie-out"
    >
      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {t("plImpact.componentTieOut.label", {
          defaultValue: "Hareket Mutabakatı",
        })}
      </h4>
      <div className="overflow-x-auto">
        <Table data-testid="pl-component-tie-out-table">
          <TableHeader>
            <TableRow>
              <TableHead>
                {t("plImpact.componentTieOut.component", {
                  defaultValue: "Bileşen",
                })}
              </TableHead>
              <TableHead className="text-right">
                {t("plImpact.componentTieOut.pl", { defaultValue: "P&L" })}
              </TableHead>
              <TableHead className="text-right">
                {t("plImpact.componentTieOut.movement", {
                  defaultValue: "Hareket",
                })}
              </TableHead>
              <TableHead className="text-right">
                {t("plImpact.componentTieOut.diff", { defaultValue: "Fark" })}
              </TableHead>
              <TableHead className="text-center w-12">
                {t("plImpact.componentTieOut.status", {
                  defaultValue: "Durum",
                })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const diff = row.plAmount - row.movementAmount;
              const pass = Math.abs(diff) <= PL_TIE_OUT_TOLERANCE;
              return (
                <TableRow key={row.testId} data-testid={row.testId}>
                  <TableCell className="text-sm">
                    {row.label}
                    <span className="ml-2 text-xs italic text-muted-foreground">
                      ({row.movementLabel})
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-right font-mono tabular-nums">
                    {formatSigned(row.plAmount.toFixed(2), currency)}
                  </TableCell>
                  <TableCell className="text-sm text-right font-mono tabular-nums">
                    {formatSigned(row.movementAmount.toFixed(2), currency)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-sm text-right font-mono tabular-nums",
                      !pass && "text-amber-700 dark:text-amber-300",
                    )}
                  >
                    {formatSigned(diff.toFixed(2), currency)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-sm text-center font-bold",
                      pass
                        ? "text-green-700 dark:text-green-300"
                        : "text-amber-700 dark:text-amber-300",
                    )}
                    data-testid={`${row.testId}-status`}
                  >
                    {pass ? "✓" : "⚠"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

// ─── EXCEL-UI-ENHANCE-01 Item 8 (UI) — per-contract breakdown accordion ────

/**
 * Collapsed-by-default shadcn Accordion surfacing the same
 * `plImpact.perContractBreakdown` table the Excel side renders at the
 * bottom of "3. P&L Etkisi". Engagement-level numbers stay the headline
 * surface; this is the drill-down for auditors who need to attribute
 * the net to specific contracts. Renders only when the breakdown has
 * at least one row.
 */
function PerContractBreakdownAccordion({
  breakdown,
  currency,
}: {
  breakdown: PLImpactSummary["perContractBreakdown"];
  currency: string;
}) {
  const { t } = useTranslation();
  return (
    <section
      className="mt-6 pt-4 border-t"
      data-testid="pl-per-contract-section"
    >
      <Accordion type="single" collapsible>
        <AccordionItem value="per-contract" className="border-b-0">
          <AccordionTrigger
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
            data-testid="pl-per-contract-trigger"
          >
            {t("plImpact.perContract.label", {
              defaultValue: "Sözleşme Bazında Dağılım",
            })}
            <span className="ml-2 text-xs font-normal italic">
              ({breakdown.length})
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="overflow-x-auto">
              <Table
                className="min-w-max"
                data-testid="pl-per-contract-table"
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("plImpact.perContract.contract", {
                        defaultValue: "Sözleşme",
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.expenses.interestExpense")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.expenses.depreciationExpense")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.expenses.variableLeaseExpense")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.gainsLosses.modificationGainLoss")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.gainsLosses.terminationGainLoss")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("plImpact.gainsLosses.fxGainLoss")}
                    </TableHead>
                    <TableHead className="text-right font-bold">
                      {t("plImpact.netEffect.label")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdown.map((c) => (
                    <TableRow
                      key={c.contractId}
                      data-testid={`pl-per-contract-row-${c.contractId}`}
                    >
                      <TableCell className="text-sm">{c.contractId}</TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatCurrency(c.interest, currency)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatCurrency(c.depreciation, currency)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatCurrency(c.variableExpense, currency)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatSigned(c.modGainLoss, currency)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatSigned(c.terminationGainLoss, currency)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono tabular-nums">
                        {formatSigned(c.fxGainLoss, currency)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-sm text-right font-mono tabular-nums font-bold",
                          Number(c.net) < 0 && "text-destructive",
                        )}
                      >
                        {formatSigned(c.net, currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse a backend Decimal string into a Number. Backend always emits
 * `toFixed(2)` for P&L amounts so precision loss is bounded at the
 * cent. Used for the client-side reconciliation arithmetic only;
 * display values still flow through the original strings.
 */
function sumStrings(values: string[]): number {
  let acc = 0;
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n)) acc += n;
  }
  return acc;
}

/**
 * EXCEL-UI-ENHANCE-01 Item 9 (UI) — read a movement-row amount as a
 * Number with safe fallback. Movement aggregator emits canonical row
 * types even when the engine produced no flow for them; we still want
 * to compare a "0" cleanly against the P&L surface.
 */
function findMovementAmount(
  rows: ReadonlyArray<{ type: MovementRowType; amount: string }>,
  rowType: MovementRowType,
): number {
  const amount = rows.find((r) => r.type === rowType)?.amount ?? "0";
  const n = Number(amount);
  return Number.isFinite(n) ? n : 0;
}

/**
 * EXCEL-UI-ENHANCE-01 Item 10 (UI) — compute the §23 / §28 FX P&L
 * components. Mirror of `plImpactSheet.ts sumFxMovementRow` + the
 * subsequent negation, but in Number-space so the UI can compose the
 * values inline. Sums each MovementRowType across ROU Cost + Liability
 * (ROU AccDep carries no FX per IAS 21.23(b)), then negates so the
 * breakdown's sign matches the P&L gain/loss convention. By
 * construction `remeasurement + revaluation == pl.fxGainLoss` to the
 * cent on the same engagement.
 */
function computeFxBreakdown(
  movement: NonNullable<EngagementAggregations["movement"]>,
): { remeasurement: number; revaluation: number } {
  const sumRow = (rowType: "FX_REMEASUREMENT" | "FX_REVALUATION"): number => {
    const rouCostN =
      Number(
        movement.rouAssetCost.total.rows.find((r) => r.type === rowType)
          ?.amount ?? "0",
      ) || 0;
    const liabN =
      Number(
        movement.leaseLiability.rows.find((r) => r.type === rowType)?.amount ??
          "0",
      ) || 0;
    return rouCostN + liabN;
  };
  // Negate to convert liability/ROU movement signs → P&L gain/loss sign.
  return {
    remeasurement: -sumRow("FX_REMEASUREMENT"),
    revaluation: -sumRow("FX_REVALUATION"),
  };
}
